import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/FishingDB';
import { CatchLog, FishingArea } from '../entities/FishingEntities';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import * as SpeciesService from '../services/SpeciesService';
import type { SpeciesWithTranslation } from '../services/SpeciesService';
import { convertWeight, convertLength, getLengthUnit, getWeightUnit } from '../utils/unitConversion';
import { FilterBar } from '../components/FilterBar';
import { Toolbar } from '../components/Toolbar';
import { BulkActions } from '../components/BulkActions';
import { CollapsibleGroup } from '../components/CollapsibleGroup';
import * as ExportService from '../services/ExportService';
import { filterCatchesWithDexie, getFilterMaxValues } from '../services/CatchFilterService';
import { 
  CatchFilters, 
  GroupOption, 
  SortOption, 
  ViewMode,
  sortCatches,
  groupCatches,
  getDefaultFilters
} from '../entities/CatchesPageTypes';
import '../App.css';
import '../styles/Pages.css';

export const CatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { unitSystem } = useSettings();

  // Data state
  const [catches, setCatches] = useState<CatchLog[]>([]);
  const [filteredCatches, setFilteredCatches] = useState<CatchLog[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [areas, setAreas] = useState<FishingArea[]>([]);
  const [speciesList, setSpeciesList] = useState<SpeciesWithTranslation[]>([]);
  const [loading, setLoading] = useState(true);
  const [maxWeight, setMaxWeight] = useState(10000);
  const [maxLength, setMaxLength] = useState(1500);
  const [currentFilterId, setCurrentFilterId] = useState<string | null>(null);

  // Filter & View state
  const [filters, setFilters] = useState<CatchFilters>(getDefaultFilters());
  const [groupBy, setGroupBy] = useState<GroupOption>('date');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Selection state
  const [selectionActive, setSelectionActive] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Load species
  const loadSpecies = useCallback(async () => {
    try {
      const allSpecies = await SpeciesService.getAllSpecies(
        (key: string) => t[key as keyof typeof t] as string,
        language
      );
      setSpeciesList(allSpecies);
    } catch (error) {
      console.error('Error loading species:', error);
    }
  }, [t, language]);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSpecies();
  }, [language, loadSpecies]);

  const loadData = async () => {
    try {
      const [catchData, areaData] = await Promise.all([
        db.catches.orderBy('timestamp').reverse().toArray(),
        db.areas.toArray()
      ]);
      setCatches(catchData);
      setAreas(areaData);
      
      // Auto-expand first 3 groups by default
      const firstThreeGroups = new Set(['0', '1', '2']);
      setExpandedGroups(firstThreeGroups);
    } catch (error) {
      console.error('Failed to load catches:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters with Dexie (async)
  useEffect(() => {
    const applyFilters = async () => {
      setIsFiltering(true);
      try {
        const filtered = await filterCatchesWithDexie(filters);
        setFilteredCatches(filtered);
        
        // Update max values from filtered results
        const maxVals = await getFilterMaxValues();
        setMaxWeight(maxVals.maxWeight);
        setMaxLength(maxVals.maxLength);
      } catch (error) {
        console.error('Failed to filter catches:', error);
        setFilteredCatches([]);
      } finally {
        setIsFiltering(false);
      }
    };
    
    applyFilters();
  }, [filters]);

  // Sort and group filtered catches
  const processedCatches = useMemo(() => {
    let sorted = sortCatches(filteredCatches, sortBy);
    return groupCatches(sorted, groupBy);
  }, [filteredCatches, sortBy, groupBy]);

  const groupedCatches = useMemo(() => {
    if (groupBy === 'none') {
      return [{
        groupKey: 'all',
        groupTitle: t.catches,
        catches: filteredCatches,
        stats: {
          totalCatches: filteredCatches.length,
          caughtCount: filteredCatches.filter(c => c.caught).length,
          contactCount: filteredCatches.filter(c => !c.caught).length,
          totalWeight: filteredCatches.reduce((sum, c) => sum + (c.weight || 0), 0),
          speciesBreakdown: []
        }
      }];
    }
    return processedCatches;
  }, [filteredCatches, processedCatches, groupBy, t]);

  // Helper functions
  const getSpeciesName = (speciesKey: string): string => {
    const species = speciesList.find(s => s.key === speciesKey);
    return species ? species.name : speciesKey;
  };

  const formatWeight = (grams: number | undefined): string | null => {
    if (!grams) return null;
    const converted = convertWeight(grams, unitSystem);
    const unit = getWeightUnit(unitSystem);
    return `${converted.toFixed(converted < 1 ? 2 : 1)} ${unit}`;
  };

  const formatLength = (mm: number | undefined): string | null => {
    if (!mm) return null;
    const converted = convertLength(mm, unitSystem);
    const unit = getLengthUnit(unitSystem);
    return `${Math.round(converted)} ${unit}`;
  };

  const handleCatchClick = (catchLog: CatchLog) => {
    if (selectionActive) {
      toggleSelection(catchLog.id);
    } else {
      navigate(`/catches/${catchLog.id}`);
    }
  };

  // Selection handlers
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    try {
      const idsToDelete = Array.from(selectedIds);
      await db.catches.bulkDelete(idsToDelete);
      
      // Reload data
      await loadData();
      
      // Clear selection
      setSelectedIds(new Set());
      setSelectionActive(false);
    } catch (error) {
      console.error('Failed to delete catches:', error);
      alert(t.error || 'Failed to delete catches');
    }
  };

  const handleBulkExport = async () => {
    try {
      // For now, export all catches (could be filtered to selected only)
      await ExportService.downloadCSV();
    } catch (error) {
      console.error('Failed to export catches:', error);
      alert(t.error || 'Failed to export catches');
    }
  };

  const handleCancelSelection = () => {
    setSelectionActive(false);
    setSelectedIds(new Set());
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Render catch card
  const renderCatchCard = (catchLog: CatchLog) => {
    const isSelected = selectedIds.has(catchLog.id);
    
    return (
      <div 
        key={catchLog.id} 
        className={`catch-item ${isSelected ? 'selected' : ''}`}
        onClick={() => handleCatchClick(catchLog)}
        style={{ cursor: 'pointer', position: 'relative' }}
      >
        {selectionActive && (
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: `2px solid ${isSelected ? '#1971c2' : '#dee2e6'}`,
            background: isSelected ? '#1971c2' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '14px',
            zIndex: 1
          }}>
            {isSelected && '✓'}
          </div>
        )}
        
        <div className="catch-icon">
          {catchLog.caught ? '🎣' : '💨'}
        </div>
        
        <div className="catch-info">
          <div className="catch-species">{getSpeciesName(catchLog.species)}</div>
          <div className="catch-details">
            {new Date(catchLog.timestamp).toLocaleString()}
            {formatWeight(catchLog.weight) && ` • ${formatWeight(catchLog.weight)}`}
            {formatLength(catchLog.length) && ` • ${formatLength(catchLog.length)}`}
          </div>
          {catchLog.notes && (
            <div className="catch-notes">{catchLog.notes}</div>
          )}
        </div>
        
        {catchLog.photo && (
          <div style={{
            width: '60px',
            height: '60px',
            marginLeft: '12px',
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0
          }}>
            <img 
              src={catchLog.photo} 
              alt={catchLog.species}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-white/70">
        <p>{t.loadingCatches}</p>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 style={{ color: 'white' }}>🎣 {t.catches}</h1>
          <p className="subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {catches.length} {t.totalCatches} • {catches.filter(c => c.caught).length} {t.landed} • {catches.filter(c => !c.caught).length} {t.released}
            {isFiltering && <span className="ml-2">⏳ {t.loading}...</span>}
          </p>
        </div>
      </div>

      {catches.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎣</div>
          <h2>{t.noCatchesYet}</h2>
          <p>{t.noCatchesDescription}</p>
        </div>
      ) : (
        <>
          {/* Filter Bar */}
          <FilterBar
            filters={filters}
            onFilterChange={setFilters}
            onClear={() => setFilters(getDefaultFilters())}
            species={speciesList}
            areas={areas}
            sortBy={sortBy}
            onSortChange={setSortBy}
            groupBy={groupBy}
            onGroupChange={setGroupBy}
            maxWeight={maxWeight}
            maxLength={maxLength}
            currentFilterId={currentFilterId}
            onFilterSelect={(filterId, filters) => {
              setCurrentFilterId(filterId);
              setFilters(filters);
            }}
          />

          {/* Toolbar - Kan tas bort nu när allt är i FilterBar */}
          <Toolbar
            groupBy={groupBy}
            sortBy={sortBy}
            viewMode={viewMode}
            onGroupByChange={setGroupBy}
            onSortByChange={setSortBy}
            onViewModeChange={setViewMode}
            selectionMode={selectionActive}
            onToggleSelection={() => setSelectionActive(!selectionActive)}
          />

          {/* Results Count */}
          {/* Results Count */}
          <div style={{ padding: '12px 16px', color: '#868e96', fontSize: '14px' }}>
            {processedCatches.length} {processedCatches.length === 1 ? t.catch : t.catches}
            {processedCatches.length !== catches.length && ` (${t.filters})`}
            {selectionActive && selectedIds.size > 0 && ` • ${selectedIds.size} ${t.selected}`}
          </div>
          {/* Grouped Catches */}
          <div className="catches-container" style={{ paddingBottom: selectionActive ? '100px' : '16px' }}>
            {groupedCatches.map((group, index) => (
              <CollapsibleGroup
                key={group.groupKey}
                group={group}
                isExpanded={expandedGroups.has(index.toString())}
                onToggle={() => toggleGroup(index.toString())}
                renderCatchCard={renderCatchCard}
              />
            ))}
          </div>

          {/* Bulk Actions */}
          {selectionActive && selectedIds.size > 0 && (
            <BulkActions
              selectedCount={selectedIds.size}
              onDelete={handleBulkDelete}
              onExport={handleBulkExport}
              onCancel={handleCancelSelection}
            />
          )}
        </>
      )}
    </>
  );
};
